import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store';
import { selectConstant } from '@/store/slices/constantsSlice';
import { useWatchHistory } from '@/hooks/useWatchHistory';
import { useHydratedMedia, type MediaRef } from '@/hooks/useHydratedMedia';
import { mediaKey } from '@/store/slices/mediaSlice';
import { ContinueWatchingCard } from './ContinueWatchingCard';
import { entryHydrationKey, filterEligibleEntries } from './filterEligibleEntries';

const MAX_VISIBLE = 12;
const SKELETON_COUNT = 3;

export function ContinueWatchingShelf(): JSX.Element | null {
  const { t } = useTranslation();
  const { entries, status } = useWatchHistory();
  const resumeMinProgressMs = useAppSelector((s) => selectConstant(s, 'resumeMinProgressMs'));
  const nearCompleteThreshold = useAppSelector((s) => selectConstant(s, 'nearCompleteThreshold'));

  const refs = useMemo<MediaRef[]>(() => {
    const out: MediaRef[] = [];
    for (const entry of entries) {
      if (entry.movieId !== null) out.push({ type: 'movie', id: entry.movieId });
      else if (entry.tvId !== null) out.push({ type: 'tv', id: entry.tvId });
    }
    return out;
  }, [entries]);

  const hydrated = useHydratedMedia(refs);

  const eligible = useMemo(
    () =>
      filterEligibleEntries(entries, hydrated, { resumeMinProgressMs, nearCompleteThreshold })
        .slice(0, MAX_VISIBLE),
    [entries, hydrated, resumeMinProgressMs, nearCompleteThreshold],
  );

  // Loading + nothing cached → skeleton row to occupy the space.
  if (status === 'loading' && entries.length === 0) {
    return (
      <section className="continue-shelf" aria-label={t('continueWatching.title')}>
        <h2 className="continue-shelf__title">{t('continueWatching.title')}</h2>
        <div className="continue-shelf__row" data-testid="continue-shelf-skeleton">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className="continue-card continue-card--skeleton" aria-hidden="true">
              <div className="continue-card__thumb">
                <div className="continue-card__skeleton" />
              </div>
              <div className="continue-card__title">&nbsp;</div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (eligible.length === 0) {
    return (
      <section className="continue-shelf" aria-label={t('continueWatching.title')}>
        <p className="continue-shelf__empty">{t('continueWatching.empty')}</p>
      </section>
    );
  }

  return (
    <section className="continue-shelf" aria-label={t('continueWatching.title')}>
      <h2 className="continue-shelf__title">{t('continueWatching.title')}</h2>
      <div className="continue-shelf__row">
        {eligible.map((entry) => {
          const key = entryHydrationKey(entry);
          const hydration = key !== null ? hydrated.get(key) : undefined;
          const media = hydration?.status === 'ready' ? hydration.data : undefined;
          const itemKey = key ?? `${entry.movieId}:${entry.tvId}:${entry.lastUpdated}`;
          return (
            <ContinueWatchingCard
              key={itemKey + ':' + (entry.seasonNumber ?? '') + ':' + (entry.episodeNumber ?? '')}
              entry={entry}
              media={media}
            />
          );
        })}
      </div>
    </section>
  );
}

// Re-exported for tests that want to seed the cache by key.
export { mediaKey };
